<template>
  <div
    ref="chartWrapper"
    class="funnel-chart-wrapper"
    :class="{ 'pointer-events-none': noInteractions }"
    tabindex="0"
    :aria-label="chartDescription"
    role="graphics-object"
    :aria-roledescription="$t('Wcag.FunnelChart')"
    @mousemove="updateTooltipPosition"
    @mouseleave="hideTooltip"
  >
    <!-- Header con titolo e legenda -->
    <div v-if="title || showLegend" class="funnel-header">
      <div>
        <Txt v-if="title" class="funnel-title">{{ title }}</Txt>
        <Txt v-if="description" small >{{ description }}</Txt>
      </div>
      <div v-if="showLegend" class="funnel-legend">
        <div
          v-for="(item, index) in chartData"
          :key="index"
          class="funnel-legend-item"
        >
          <span
            class="funnel-legend-dot"
            :style="{ backgroundColor: item.color }"
          />
          <span class="funnel-legend-label">{{ $t('FunnelChart.Legend.'+item.label) }}</span>
        </div>
      </div>
    </div>

    <!-- SVG Funnel -->
    <div ref="svgContainer" class="funnel-svg-container">
      <svg
        :viewBox="`0 0 ${svgWidth} ${svgHeight}`"
        preserveAspectRatio="xMidYMid meet"
        class="funnel-svg"
      >
        <defs>
          <linearGradient
            v-for="(item, index) in chartData"
            :id="`funnel-gradient-${uid}-${index}`"
            :key="`gradient-${index}`"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" :stop-color="item.color" stop-opacity="1" />
            <stop offset="100%" :stop-color="adjustColor(item.color, -20)" stop-opacity="1" />
          </linearGradient>
        </defs>

        <g
          v-for="(section, index) in funnelSections"
          :key="index"
          class="funnel-section"
          @mouseenter="handleHover(index)"
          @mouseleave="handleHover(null)"
          @click="handleClick(index)"
        >
          <path
            :d="section.path"
            :fill="`url(#funnel-gradient-${uid}-${index})`"
            :opacity="hoveredIndex === null || hoveredIndex === index ? 1 : 0.6"
            class="funnel-path"
          />
          <text
            v-if="showValues"
            :x="section.centerX"
            :y="section.centerY"
            text-anchor="middle"
            dominant-baseline="middle"
            class="funnel-value"
            :fill="getContrastColor(chartData[index].color)"
          >
            {{ formatValue(chartData[index].value) }}
          </text>
        </g>
      </svg>
    </div>

    <!-- Percentuali di conversione -->
    <div v-if="showConversionRates && conversionRates.length" class="funnel-conversions">
      <div
        v-for="(rate, index) in conversionRates"
        :key="index"
        class="funnel-conversion-item"
      >
        <span class="funnel-conversion-label">
          {{ chartData[index].label }} → {{ chartData[index + 1].label }}
        </span>
        <span class="funnel-conversion-rate" :style="{ color: chartData[index].color }">
          {{ rate }}
        </span>
      </div>
    </div>

    <div class="text-xs italic text-gray-700 absolute pt-2 right-2">{{ $t('FunnelChart.DataRange') }}</div>

    <!-- Tooltip -->
    <div
      v-if="showTooltip && hoveredIndex !== null && tooltipVisible"
      ref="tooltipEl"
      class="funnel-tooltip"
      :style="tooltipStyle"
    >
      <div class="funnel-tooltip-label">{{ $t('FunnelChart.Legend.'+chartData[hoveredIndex].label) }}</div>
      <div class="funnel-tooltip-row">
        <span class="funnel-tooltip-dot" :style="{ backgroundColor: chartData[hoveredIndex].color }" />
        <span class="funnel-tooltip-value">{{ chartData[hoveredIndex].value }}</span>
      </div>
      <div class="funnel-tooltip-percentage">
        {{ formatPercentage(chartData[hoveredIndex].percentage) }} {{ $t('FunnelChart.OfTotal') }}
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import colors from '@/utils/colors';
import { mergeObjects } from '@/utils/object';

const defaultColors = [
  colors.indigo[700],
  colors.green[500],
  colors.accent[600],
  colors.cyan[600]
];

export default defineComponent({
  name: 'FunnelChart',

  props: {
    data: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      default: undefined,
    },
    description: {
      type: String,
      default: undefined,
    },
    showLegend: {
      type: Boolean,
      default: false,
    },
    showValues: {
      type: Boolean,
      default: true,
    },
    showConversionRates: {
      type: Boolean,
      default: true,
    },
    conversions: {
      type: Object,
      default: () => ({}),
    },
    showTooltip: {
      type: Boolean,
      default: true,
    },
    noInteractions: {
      type: Boolean,
      default: false,
    },
    height: {
      type: Number,
      default: 300,
    },
    aspectRatio: {
      type: Number,
      default: undefined,
    },
    proportional: {
      type: Boolean,
      default: true,
    },
    chartOptions: {
      type: Object,
      default: undefined,
    },
  },

  setup(props) {
    const chartWrapper = ref(null);
    const svgContainer = ref(null);
    const tooltipEl = ref(null);
    const hoveredIndex = ref(null);
    const tooltipVisible = ref(false);
    const containerWidth = ref(600);
    const uid = ref(`funnel-${Math.random().toString(36).substr(2, 9)}`);

    // Posizione del tooltip
    const tooltipPosition = ref({ x: 0, y: 0 });

    const config = computed(() => {
      const defaults = {
        colors: defaultColors,
        baseWidthRatio: 0.3,
        sectionGap: 2,
      };
      return mergeObjects(defaults, props.chartOptions || {});
    });

    const svgWidth = computed(() => containerWidth.value);
    const svgHeight = computed(() => {
      if (props.aspectRatio) {
        return containerWidth.value / props.aspectRatio;
      }
      return props.height;
    });

    const chartData = computed(() => {
      const maxValue = Math.max(...props.data.map(d => d.value));
      return props.data.map((item, index) => ({
        ...item,
        color: item.color || config.value.colors[index % config.value.colors.length],
        percentage: maxValue > 0 ? (item.value / maxValue) * 100 : 0,
      }));
    });

    const normalizeLabelKey = (label) => {
      if (!label) return '';
      let key = String(label).toLowerCase().trim();
      key = key.replace(/\s+/g, '_');
      if (key === 'open' || key === 'opens') key = 'opened';
      if (key === 'click' || key === 'clicks') key = 'clicked';
      return key;
    };

    const conversionRates = computed(() => {
      const rates = [];
      const dataLen = chartData.value.length;

      const hasConversions = props.conversions && Object.keys(props.conversions).length > 0;

      for (let i = 0; i < dataLen - 1; i++) {
        if (hasConversions) {
          const from = normalizeLabelKey(chartData.value[i].label);
          const to = normalizeLabelKey(chartData.value[i + 1].label);
          const key = `${from}_to_${to}`;
          const val = props.conversions[key];
          if (typeof val !== 'undefined' && val !== null) {
            rates.push(val);
            continue;
          }
        }

        // Fallback: compute from values when conversions not present for this step
        const current = chartData.value[i].value;
        const next = chartData.value[i + 1].value;
        rates.push(current > 0 ? (next / current) * 100 : 0);
      }

      return rates;
    });

    const funnelSections = computed(() => {
      const sections = [];
      const width = svgWidth.value;
      const height = svgHeight.value;
      const dataLength = chartData.value.length;

      if (dataLength === 0) return sections;

      const padding = 20;
      const availableWidth = width - padding * 2;
      const availableHeight = height - padding * 2;

      const sectionHeight = (availableHeight - (config.value.sectionGap * (dataLength - 1))) / dataLength;
      const centerX = width / 2;
      const minWidthRatio = config.value.baseWidthRatio;

      let widths;

      if (props.proportional) {
        // Modalità proporzionale: larghezza basata sui valori
        const maxValue = Math.max(...chartData.value.map(d => d.value));
        widths = chartData.value.map(d => {
          const ratio = maxValue > 0 ? d.value / maxValue : 1;
          // Scala tra minWidthRatio e 1 per evitare sezioni troppo strette
          const scaledRatio = minWidthRatio + (1 - minWidthRatio) * ratio;
          return availableWidth * scaledRatio;
        });
      } else {
        // Modalità standard: decrescita costante
        const topWidth = availableWidth;
        const bottomWidth = availableWidth * minWidthRatio;
        const widthStep = (topWidth - bottomWidth) / dataLength;
        widths = chartData.value.map((_, i) => topWidth - widthStep * i);
      }

      for (let i = 0; i < dataLength; i++) {
        const y1 = padding + i * (sectionHeight + config.value.sectionGap);
        const y2 = y1 + sectionHeight;

        const topWidth = widths[i];
        const bottomWidth = i < dataLength - 1 ? widths[i + 1] : widths[i] * minWidthRatio;

        const topHalfWidth = topWidth / 2;
        const bottomHalfWidth = bottomWidth / 2;

        const x1Top = centerX - topHalfWidth;
        const x2Top = centerX + topHalfWidth;
        const x1Bottom = centerX - bottomHalfWidth;
        const x2Bottom = centerX + bottomHalfWidth;

        const path = `M ${x1Top} ${y1} L ${x2Top} ${y1} L ${x2Bottom} ${y2} L ${x1Bottom} ${y2} Z`;

        sections.push({
          path,
          centerX,
          centerY: (y1 + y2) / 2,
        });
      }

      return sections;
    });

    // Stile del tooltip con posizionamento dinamico
    const tooltipStyle = computed(() => {
      return {
        left: `${tooltipPosition.value.x}px`,
        top: `${tooltipPosition.value.y}px`,
      };
    });

    const formatValue = (value) => {
      const isCompact = value >= 10000;
      return new Intl.NumberFormat('it-IT', {
        notation: isCompact ? 'compact' : 'standard',
        maximumFractionDigits: isCompact ? 1 : 0,
      }).format(value);
    };

    const formatPercentage = (value) => {
      return new Intl.NumberFormat('it-IT', {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(value / 100);
    };

    const getContrastColor = (hexColor) => {
      const hex = hexColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? colors.primary[700] : '#ffffff';
    };

    const adjustColor = (hexColor, amount) => {
      const hex = hexColor.replace('#', '');
      const r = Math.max(0, Math.min(255, parseInt(hex.substr(0, 2), 16) + amount));
      const g = Math.max(0, Math.min(255, parseInt(hex.substr(2, 2), 16) + amount));
      const b = Math.max(0, Math.min(255, parseInt(hex.substr(4, 2), 16) + amount));
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    const handleHover = (index) => {
      if (props.noInteractions) return;
      hoveredIndex.value = index;
      tooltipVisible.value = index !== null;
    };

    const updateTooltipPosition = async (event) => {
      if (!props.showTooltip || hoveredIndex.value === null || props.noInteractions) return;

      const wrapperRect = chartWrapper.value?.getBoundingClientRect();
      if (!wrapperRect) return;

      // Calcola posizione relativa al wrapper
      let x = event.clientX - wrapperRect.left + 15;
      let y = event.clientY - wrapperRect.top + 15;

      // Attendi il render del tooltip per ottenere le sue dimensioni
      await nextTick();

      if (tooltipEl.value) {
        const tooltipRect = tooltipEl.value.getBoundingClientRect();

        // Evita che il tooltip esca dal wrapper a destra
        if (x + tooltipRect.width > wrapperRect.width) {
          x = event.clientX - wrapperRect.left - tooltipRect.width - 15;
        }

        // Evita che il tooltip esca dal wrapper in basso
        if (y + tooltipRect.height > wrapperRect.height) {
          y = event.clientY - wrapperRect.top - tooltipRect.height - 15;
        }
      }

      tooltipPosition.value = { x, y };
    };

    const hideTooltip = () => {
      tooltipVisible.value = false;
      hoveredIndex.value = null;
    };

    const handleClick = (index) => {
      if (props.noInteractions) return;
      svgContainer.value?.dispatchEvent(new CustomEvent('section-click', {
        detail: chartData.value[index],
        bubbles: true,
      }));
    };

    let resizeObserver = null;

    onMounted(() => {
      if (svgContainer.value) {
        containerWidth.value = svgContainer.value.offsetWidth || 600;

        resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
              const newWidth = entry.contentRect.width || 600;
              if (newWidth === containerWidth.value || (newWidth - containerWidth.value) < 1) continue
              containerWidth.value = newWidth;
          }
        });
        resizeObserver.observe(svgContainer.value);
      }
    });

    onUnmounted(() => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    });

    return {
      chartWrapper,
      svgContainer,
      tooltipEl,
      svgWidth,
      svgHeight,
      chartData,
      conversionRates,
      funnelSections,
      hoveredIndex,
      tooltipVisible,
      tooltipPosition,
      tooltipStyle,
      uid,
      formatValue,
      formatPercentage,
      getContrastColor,
      adjustColor,
      handleHover,
      handleClick,
      updateTooltipPosition,
      hideTooltip,
    };
  },

  computed: {
    chartDescription() {
      const dataDesc = this.chartData
        .map(d => `${d.label}: ${this.formatValue(d.value)}`)
        .join(', ');
      return `${this.$t('Wcag.FunnelChart')}, ${this.title || ''}: ${dataDesc}`;
    },
  },
});
</script>

<style lang="scss" scoped>
.funnel-chart-wrapper {
  @apply relative w-full h-full;
}

.funnel-header {
  @apply flex flex-wrap items-center justify-between gap-4 mb-4;
}

.funnel-title {
  @apply text-sm font-bold text-primary-700 mb-2;
}

.funnel-legend {
  @apply flex flex-wrap gap-4;
}

.funnel-legend-item {
  @apply flex items-center gap-2;
}

.funnel-legend-dot {
  @apply w-2.5 h-2.5 rounded-full flex-shrink-0;
}

.funnel-legend-label {
  @apply text-sm text-primary-700;
}

.funnel-svg-container {
  @apply w-full flex justify-center;
}

.funnel-svg {
  @apply w-full h-auto max-w-full;
}

.funnel-section {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.funnel-path {
  transition: opacity 0.2s ease;
}

.funnel-value {
  @apply font-bold text-base;
  font-family: inherit;
  pointer-events: none;
}

.funnel-conversions {
  @apply flex flex-wrap justify-center gap-6 mt-6 pt-4 border-t border-primary-100;
}

.funnel-conversion-item {
  @apply flex flex-col items-center text-center;
}

.funnel-conversion-label {
  @apply text-xs text-primary-300 mb-1;
}

.funnel-conversion-rate {
  @apply text-lg font-bold;
}

/* Tooltip - stile coerente con Chart.js */
.funnel-tooltip {
  @apply absolute z-50 rounded-md pointer-events-none;
  background-color: #054fb9; /* colors['soft-blue'][700] */
  padding: 12px;
  font-size: 14px;
  font-weight: 700;
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 120px;
}

.funnel-tooltip-label {
  @apply mb-2 pb-2 border-b border-white/20;
}

.funnel-tooltip-row {
  @apply flex items-center gap-2;
}

.funnel-tooltip-dot {
  @apply w-2.5 h-2.5 rounded-full flex-shrink-0;
}

.funnel-tooltip-value {
  @apply text-base;
}

.funnel-tooltip-percentage {
  @apply text-xs opacity-80 mt-1;
}

@media (max-width: 640px) {
  .funnel-header {
    @apply flex-col items-start;
  }

  .funnel-conversions {
    @apply flex-col items-center gap-3;
  }

  .funnel-value {
    @apply text-sm;
  }
}
</style>
